/**
 * ArcWalkerDrawer — Story Arc Walker results panel.
 *
 * Uses async background job pattern:
 * - POST /api/projects/{pid}/arc-walker  → starts job, returns {job_id}
 * - GET  /api/projects/{pid}/arc-walker/status → polls until done/failed
 *
 * Triggered both on demand (toolbar button) and automatically
 * after each chapter AI generation.
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Bold,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Loader2,
  Mic,
  RefreshCw,
  User,
  XCircle,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import axios from 'axios';
import { usePW } from '@/lib/store';

// ── Per-panel reading prefs ───────────────────────────────────────────────────
const ARC_FONT_SIZES = [
  { key: 'sm', label: 'S', body: '11px',   heading: '12.5px' },
  { key: 'md', label: 'M', body: '11.5px', heading: '13px' },
  { key: 'lg', label: 'L', body: '13px',   heading: '14.5px' },
  { key: 'xl', label: 'XL',body: '15px',   heading: '16px' },
];
const ARC_LS_KEY = 'loom:reading_prefs_arc';
function loadArcPrefs() {
  try { return JSON.parse(localStorage.getItem(ARC_LS_KEY) || '{}'); } catch { return {}; }
}
function saveArcPrefs(p) { localStorage.setItem(ARC_LS_KEY, JSON.stringify(p)); }

function ArcReadingControls({ prefs, setPrefs }) {
  const idx = Math.max(0, ARC_FONT_SIZES.findIndex((f) => f.key === prefs.fontSize));
  const cycle = (dir) => {
    const next = Math.max(0, Math.min(ARC_FONT_SIZES.length - 1, idx + dir));
    const u = { ...prefs, fontSize: ARC_FONT_SIZES[next].key };
    setPrefs(u); saveArcPrefs(u);
  };
  const toggleBold = () => {
    const u = { ...prefs, bold: !prefs.bold };
    setPrefs(u); saveArcPrefs(u);
  };
  return (
    <div className="flex items-center gap-1">
      <button type="button" onClick={() => cycle(-1)} disabled={idx === 0}
        className="flex h-6 w-6 items-center justify-center rounded-sm border border-border text-muted-foreground hover:text-foreground disabled:opacity-30 transition" title="Smaller">
        <ZoomOut className="h-3 w-3" />
      </button>
      <span className="w-5 text-center text-[13px] font-mono text-muted-foreground">{ARC_FONT_SIZES[idx].label}</span>
      <button type="button" onClick={() => cycle(1)} disabled={idx === ARC_FONT_SIZES.length - 1}
        className="flex h-6 w-6 items-center justify-center rounded-sm border border-border text-muted-foreground hover:text-foreground disabled:opacity-30 transition" title="Larger">
        <ZoomIn className="h-3 w-3" />
      </button>
      <button type="button" onClick={toggleBold}
        className={cn('flex h-6 w-6 items-center justify-center rounded-sm border transition',
          prefs.bold ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:text-foreground')}
        title={prefs.bold ? 'Disable bold' : 'Enable bold'}>
        <Bold className="h-3 w-3" />
      </button>
    </div>
  );
}

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const POLL_INTERVAL_MS = 3000;

// ── Score badge ──────────────────────────────────────────────────────────────

function ScoreBadge({ score }) {
  const grade = score >= 85 ? 'green' : score >= 70 ? 'amber' : 'red';
  return (
    <div className="flex flex-col items-center gap-1">
      <div
        className={cn(
          'flex h-20 w-20 items-center justify-center rounded-full text-3xl font-bold shadow-inner',
          grade === 'green' && 'bg-emerald-100 text-emerald-800 ring-4 ring-emerald-400/40',
          grade === 'amber' && 'bg-amber-100 text-amber-800 ring-4 ring-amber-400/40',
          grade === 'red'   && 'bg-rose-100 text-rose-800 ring-4 ring-rose-400/40',
        )}
        data-testid="arc-walker-score-circle"
      >
        {score}
      </div>
      <span className={cn(
        'text-[13px] font-semibold uppercase tracking-wide',
        grade === 'green' && 'text-emerald-700',
        grade === 'amber' && 'text-amber-700',
        grade === 'red'   && 'text-rose-700',
      )}>
        {score >= 85 ? 'Publishable' : score >= 70 ? 'Needs Work' : 'Not Ready'}
      </span>
    </div>
  );
}

// ── Publishable threshold bar ─────────────────────────────────────────────────
// Visual progress toward the 85% publishable threshold.

const THRESHOLD = 85;

function ThresholdBar({ score }) {
  const pct = Math.min(score, 100);
  const thresholdPct = THRESHOLD;
  const isPublishable = score >= THRESHOLD;
  const barColor = isPublishable ? 'bg-emerald-500' : score >= 70 ? 'bg-amber-400' : 'bg-rose-400';
  const needed = THRESHOLD - score;

  return (
    <div className="w-full space-y-1.5" data-testid="arc-walker-threshold-bar">
      <div className="flex items-center justify-between text-[13px] text-muted-foreground">
        <span className="font-medium text-foreground/60">Publishable threshold</span>
        {isPublishable ? (
          <span className="flex items-center gap-1 font-semibold text-emerald-600">
            <CheckCircle2 className="h-3 w-3" /> Threshold met
          </span>
        ) : (
          <span className="font-semibold text-amber-600" data-testid="arc-walker-points-needed">
            {needed} point{needed === 1 ? '' : 's'} to Publishable
          </span>
        )}
      </div>
      {/* Progress bar with 85% threshold marker */}
      <div className="relative h-2.5 w-full overflow-hidden rounded-full bg-secondary">
        <div
          className={cn('h-full rounded-full transition-all duration-500', barColor)}
          style={{ width: `${pct}%` }}
        />
        {/* 85% target marker */}
        <div
          className="absolute top-0 bottom-0 w-px bg-foreground/30"
          style={{ left: `${thresholdPct}%` }}
          title="85 — Publishable threshold"
        />
      </div>
      <div className="flex items-center justify-between text-[9.5px] text-muted-foreground/60">
        <span>0</span>
        <span
          className={cn('font-semibold', isPublishable ? 'text-emerald-600' : 'text-foreground/50')}
          style={{ marginLeft: `${thresholdPct - 2}%` }}
        >
          85
        </span>
        <span>100</span>
      </div>
    </div>
  );
}

// ── Breakdown bars ───────────────────────────────────────────────────────────

function BreakdownBars({ breakdown }) {
  const LABELS = {
    arc_completion:        { label: 'Arc completion',    weight: '25%' },
    continuity:            { label: 'Continuity',        weight: '25%' },
    tone_consistency:      { label: 'Tone consistency',  weight: '20%' },
    character_consistency: { label: 'Character voice',   weight: '15%' },
    voice_consistency:     { label: 'Prose voice',       weight: '15%' },
  };
  return (
    <div className="space-y-2" data-testid="arc-walker-breakdown">
      {Object.entries(LABELS).map(([key, { label, weight }]) => {
        const val = breakdown?.[key] ?? 0;
        const color = val >= 85 ? 'bg-emerald-500' : val >= 70 ? 'bg-amber-500' : 'bg-rose-500';
        return (
          <div key={key} className="flex items-center gap-2">
            <span className="w-36 shrink-0 text-[13px] text-muted-foreground">{label}</span>
            <div className="flex-1 h-2 overflow-hidden rounded-full bg-secondary">
              <div className={cn('h-full transition-all', color)} style={{ width: `${val}%` }} />
            </div>
            <span className="w-8 text-right font-mono-stat text-[13px] text-foreground">{val}</span>
            <span className="w-8 text-right text-[13px] text-muted-foreground/60">{weight}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Section card ─────────────────────────────────────────────────────────────

function SectionCard({ icon: Icon, title, children, defaultOpen = true, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-md border border-panel-border bg-panel-header/40" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13.5px] font-semibold hover:bg-secondary/60 transition"
      >
        {Icon && <Icon className="h-3.5 w-3.5 shrink-0 text-primary" />}
        <span className="flex-1">{title}</span>
        {open ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
      </button>
      {open && <div className="px-3 pb-3 pt-1 text-[13.5px]">{children}</div>}
    </div>
  );
}

// ── Status pill ───────────────────────────────────────────────────────────────

function StatusPill({ status }) {
  const MAP = {
    on_track:   { label: 'On track',   cls: 'bg-emerald-100 text-emerald-800' },
    stalled:    { label: 'Stalled',    cls: 'bg-amber-100 text-amber-800' },
    missing:    { label: 'Missing',    cls: 'bg-rose-100 text-rose-800' },
    resolved:   { label: 'Resolved',  cls: 'bg-sky-100 text-sky-800' },
    unresolved: { label: 'Unresolved', cls: 'bg-rose-100 text-rose-800' },
  };
  const s = MAP[status] || { label: status, cls: 'bg-secondary text-muted-foreground' };
  return (
    <span className={cn('inline-flex rounded-sm px-1.5 py-0.5 text-[13px] font-semibold uppercase tracking-wide', s.cls)}>
      {s.label}
    </span>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function ArcWalkerDrawer({ open, onClose, projectId, cachedResult }) {
  const [loading, setLoading] = useState(false);
  const [result,  setResult]  = useState(cachedResult || null);
  const [jobError, setJobError] = useState(null);
  const pollRef = useRef(null);
  const [prefs, setPrefs] = useState(() => ({ fontSize: 'md', bold: false, ...loadArcPrefs() }));

  // ── Character arc filter ───────────────────────────────────────────────────
  // Read the character bible to identify the protagonist.  Default selected set
  // is just the protagonist's name so the drawer opens clean and focused.
  const { characters = [] } = usePW();

  const protagonist = useMemo(
    () => characters.find((c) => (c.role || '').toLowerCase().includes('protagonist')),
    [characters],
  );

  // Build the default selected-name set once `result` arrives or `protagonist` resolves.
  // Only protagonist is selected; every other arc starts unchecked.
  const [selectedCharNames, setSelectedCharNames] = useState(null); // null = not yet initialised

  useEffect(() => {
    if (!result?.character_arcs?.length) return;
    if (selectedCharNames !== null) return;           // already initialised for this result

    const arcNames = result.character_arcs.map((ca) => ca.character);
    const proto = protagonist?.name;

    // Pick the first arc whose name matches the protagonist from the bible (case-insensitive).
    // Fall back to the very first arc if none match.
    const defaultName = proto
      ? arcNames.find((n) => n.toLowerCase() === proto.toLowerCase()) || arcNames[0]
      : arcNames[0];

    setSelectedCharNames(defaultName ? new Set([defaultName]) : new Set(arcNames));
  }, [result, protagonist, selectedCharNames]);

  // Reset filter when a new Arc Walker run finishes so it re-defaults to protagonist.
  const resetCharFilter = useCallback(() => setSelectedCharNames(null), []);

  const toggleChar = (name) =>
    setSelectedCharNames((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        if (next.size === 1) return prev; // always keep at least one selected
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });

  const selectAllChars = () =>
    setSelectedCharNames(new Set((result?.character_arcs || []).map((ca) => ca.character)));

  const currentFont = ARC_FONT_SIZES.find((f) => f.key === prefs.fontSize) || ARC_FONT_SIZES[1];
  const bodyStyle = { fontSize: currentFont.body, fontWeight: prefs.bold ? 600 : 400 };
  const headingStyle = { fontSize: currentFont.heading };

  // Sync cached result from parent whenever it changes
  useEffect(() => {
    if (cachedResult) setResult(cachedResult);
  }, [cachedResult]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const startPolling = useCallback((toastId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const s = await axios.get(
          `${API}/projects/${projectId}/arc-walker/status`,
          { withCredentials: true },
        );
        const { status, result: r, error } = s.data;
        if (status === 'done' && r) {
          stopPolling();
          setResult(r);
          resetCharFilter();           // re-default to protagonist on fresh run
          setLoading(false);
          toast.dismiss(toastId);
          const score = r?.publishable_score ?? 0;
          score >= 85
            ? toast.success(`Arc Walker complete — Publishable score: ${score}/100`)
            : toast.warning(`Arc Walker complete — Score ${score}/100 (target: 85)`);
        } else if (status === 'failed') {
          stopPolling();
          setLoading(false);
          setJobError(error || 'Arc Walker failed.');
          toast.dismiss(toastId);
          toast.error(error || 'Arc Walker failed. Please try again.');
        }
        // status === 'running' → keep polling
      } catch {
        // transient network error — keep polling
      }
    }, POLL_INTERVAL_MS);
  }, [projectId, stopPolling, resetCharFilter]);

  // Cleanup on unmount
  useEffect(() => () => stopPolling(), [stopPolling]);

  const runWalker = useCallback(async () => {
    if (!projectId || loading) return;
    setLoading(true);
    setJobError(null);
    const toastId = toast.loading('Story Arc Walker running — analyzing all chapters…');
    try {
      const r = await axios.post(
        `${API}/projects/${projectId}/arc-walker`,
        {},
        { withCredentials: true, timeout: 15000 },
      );
      const { status, started } = r.data;
      if (status === 'running' && started !== false) {
        // Background job started — begin polling
        startPolling(toastId);
      } else if (status === 'running' && started === false) {
        // Already running from a previous trigger
        toast.dismiss(toastId);
        toast('A walk is already in progress — results will appear shortly.');
        startPolling(toastId);
      }
    } catch (err) {
      setLoading(false);
      toast.dismiss(toastId);
      toast.error(err?.response?.data?.detail || 'Arc Walker failed. Please try again.');
    }
  }, [projectId, loading, startPolling]);

  // Auto-run on first open if no cached result
  useEffect(() => {
    if (open && !result && !loading) {
      runWalker();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="right"
        className="overflow-y-auto scroll-thin p-0"
        style={{ width: '67vw', maxWidth: '67vw' }}
        data-testid="arc-walker-drawer"
      >
        <SheetHeader className="sticky top-0 z-10 border-b border-panel-border bg-panel-header px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <SheetTitle className="text-[14px] font-semibold">Story Arc Walker</SheetTitle>
            <div className="flex items-center gap-2">
              <ArcReadingControls prefs={prefs} setPrefs={setPrefs} />
              <Button
                variant="ghost"
                size="sm"
                className="h-7 gap-1 px-2 text-[13px]"
                onClick={runWalker}
                disabled={loading}
                data-testid="arc-walker-rerun-btn"
                title="Re-run the analysis"
              >
                {loading
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <RefreshCw className="h-3.5 w-3.5" />}
                {loading ? 'Analyzing…' : 'Re-run'}
              </Button>
            </div>
          </div>
          <p className="mt-1 text-[13px] text-muted-foreground">
            Tracks character arcs, continuity, tone &amp; voice consistency.
            Target: <strong>85%</strong> publishable threshold.
          </p>
        </SheetHeader>

        <div className="p-4 space-y-4">
          {loading && !result && (
            <div className="flex flex-col items-center gap-3 py-10 text-muted-foreground">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-center">
                Reading all chapters — analyzing in the background…
                <br />
                <span className="text-[13px] opacity-70">Results appear here automatically.</span>
              </p>
            </div>
          )}

          {!loading && !result && !jobError && (
            <div className="flex flex-col items-center gap-3 py-10 text-muted-foreground">
              <BookOpen className="h-8 w-8 opacity-40" />
              <p className="text-sm text-center">Click Re-run above to analyze your manuscript.</p>
            </div>
          )}

          {!loading && jobError && (
            <div className="rounded-md border border-rose-300 bg-rose-50 p-3 text-[13.5px] text-rose-700">
              <p className="font-medium">Arc Walker failed</p>
              <p className="mt-0.5 opacity-80">{jobError}</p>
              <Button size="sm" variant="outline" className="mt-2 h-6 text-[13px]" onClick={runWalker}>
                Try again
              </Button>
            </div>
          )}

          {result && (
            <>
              {/* Score + meta */}
              <div className="rounded-lg border border-panel-border bg-panel-header/60 p-4 space-y-4">
                <div className="flex items-center gap-5">
                  <ScoreBadge score={result.publishable_score ?? 0} />
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-1.5 text-[13.5px] font-semibold">
                      {result.publishable
                        ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                        : <XCircle className="h-4 w-4 text-rose-600" />}
                      {result.grade && <span className="font-mono-stat text-primary">{result.grade}</span>}
                      <span>{result.publishable ? 'Meets threshold' : 'Below 85% threshold'}</span>
                    </div>
                    <p className="text-[13px] text-muted-foreground leading-snug" style={bodyStyle}>
                      {result.recommendation}
                    </p>
                    <p className="text-[13px] text-muted-foreground/60" style={bodyStyle}>
                      {result.chapters_analyzed} / {result.total_chapters} chapters analyzed
                      {result.ran_at && ` · ${new Date(result.ran_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                    </p>
                  </div>
                </div>
                {/* Threshold progress bar */}
                <ThresholdBar score={result.publishable_score ?? 0} />
              </div>

              {/* Breakdown bars */}
              <SectionCard icon={BookOpen} title="Score breakdown" testId="arc-walker-score-section">
                <BreakdownBars breakdown={result.score_breakdown} />
              </SectionCard>

              {/* Character arcs — protagonist selected by default */}
              <SectionCard icon={User} title={`Character arcs (${(result.character_arcs || []).length})`} testId="arc-walker-chars-section">
                {(result.character_arcs || []).length === 0 ? (
                  <p className="text-muted-foreground">No characters detected.</p>
                ) : (
                  <>
                    {/* ── Filter chips ─────────────────────────────────────── */}
                    <div className="mb-3 flex flex-wrap items-center gap-1.5" data-testid="arc-walker-char-filter">
                      {result.character_arcs.map((ca) => {
                        const isProto = protagonist?.name?.toLowerCase() === ca.character.toLowerCase();
                        const isOn = selectedCharNames?.has(ca.character) ?? true;
                        return (
                          <button
                            key={ca.character}
                            type="button"
                            onClick={() => toggleChar(ca.character)}
                            data-testid={`arc-char-chip-${ca.character}`}
                            className={cn(
                              'flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11.5px] font-medium transition-colors',
                              isOn
                                ? 'border-primary bg-primary/10 text-primary'
                                : 'border-border bg-secondary/40 text-muted-foreground hover:border-primary/40 hover:text-foreground',
                            )}
                          >
                            {isProto && (
                              <span className="mr-0.5 text-[9px] font-bold uppercase tracking-wider opacity-70">P</span>
                            )}
                            {ca.character}
                          </button>
                        );
                      })}
                      {selectedCharNames && selectedCharNames.size < result.character_arcs.length && (
                        <button
                          type="button"
                          onClick={selectAllChars}
                          className="text-[11px] text-muted-foreground underline hover:text-foreground transition-colors"
                        >
                          Show all
                        </button>
                      )}
                    </div>

                    {/* ── Filtered arc entries ──────────────────────────────── */}
                    {result.character_arcs
                      .filter((ca) => selectedCharNames?.has(ca.character) ?? true)
                      .map((ca, i) => (
                        <div key={i} className="mb-2.5 last:mb-0">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <span className="font-medium">{ca.character}</span>
                            <StatusPill status={ca.status} />
                            {(ca.gap_chapters || []).length > 0 && (
                              <span className="text-[13px] text-amber-700">
                                missing Ch{ca.gap_chapters.join(', ')}
                              </span>
                            )}
                          </div>
                          {ca.notes && (
                            <p className="text-[13px] text-muted-foreground leading-snug">{ca.notes}</p>
                          )}
                        </div>
                      ))}
                  </>
                )}
              </SectionCard>

              {/* Story arcs */}
              <SectionCard icon={BookOpen} title={`Story arcs (${(result.story_arcs || []).length})`} defaultOpen={false} testId="arc-walker-story-section">
                {(result.story_arcs || []).length === 0
                  ? <p className="text-muted-foreground">No story arcs detected.</p>
                  : (result.story_arcs || []).map((sa, i) => (
                    <div key={i} className="mb-2.5 last:mb-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="font-medium">{sa.arc}</span>
                        <StatusPill status={sa.status} />
                      </div>
                      {sa.notes && <p className="text-[13px] text-muted-foreground leading-snug">{sa.notes}</p>}
                    </div>
                  ))
                }
              </SectionCard>

              {/* Continuity gaps */}
              <SectionCard
                icon={AlertTriangle}
                title={`Continuity gaps (${(result.continuity_gaps || []).length})`}
                defaultOpen={(result.continuity_gaps || []).length > 0}
                testId="arc-walker-gaps-section"
              >
                {(result.continuity_gaps || []).length === 0
                  ? <p className="flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> No continuity issues found.</p>
                  : (result.continuity_gaps || []).map((g, i) => {
                    const cls = g.severity === 'high' ? 'bg-rose-50 border-rose-200 text-rose-800'
                      : g.severity === 'medium' ? 'bg-amber-50 border-amber-200 text-amber-800'
                      : 'bg-sky-50 border-sky-200 text-sky-800';
                    return (
                      <div key={i} className={cn('mb-2 rounded border p-2 last:mb-0', cls)}>
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <span className="capitalize font-semibold text-[13px] uppercase tracking-wide">{g.severity}</span>
                          {(g.chapters_affected || []).length > 0 && (
                            <span className="text-[13px] opacity-70">Ch {g.chapters_affected.join(', ')}</span>
                          )}
                        </div>
                        <p className="leading-snug">{g.description}</p>
                      </div>
                    );
                  })
                }
              </SectionCard>

              {/* Drift flags */}
              <SectionCard
                icon={Mic}
                title="Tone & voice drift"
                defaultOpen={result.tone_drift?.detected || result.voice_drift?.detected}
                testId="arc-walker-drift-section"
              >
                {/* Tone */}
                <div className="mb-3">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="text-[13px] font-semibold">Tone drift</span>
                    {result.tone_drift?.detected
                      ? <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                      : <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
                    <span className={cn(
                      'text-[13px] font-semibold uppercase tracking-wide',
                      result.tone_drift?.severity === 'major' ? 'text-rose-700'
                      : result.tone_drift?.severity === 'minor' ? 'text-amber-700'
                      : 'text-emerald-700',
                    )}>
                      {result.tone_drift?.severity || 'none'}
                    </span>
                  </div>
                  {result.tone_drift?.description && (
                    <p className="text-[13px] text-muted-foreground leading-snug">{result.tone_drift.description}</p>
                  )}
                </div>
                {/* Voice */}
                <div>
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="text-[13px] font-semibold">Voice drift</span>
                    {result.voice_drift?.detected
                      ? <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                      : <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
                    <span className={cn(
                      'text-[13px] font-semibold uppercase tracking-wide',
                      result.voice_drift?.severity === 'major' ? 'text-rose-700'
                      : result.voice_drift?.severity === 'minor' ? 'text-amber-700'
                      : 'text-emerald-700',
                    )}>
                      {result.voice_drift?.severity || 'none'}
                    </span>
                  </div>
                  {result.voice_drift?.description && (
                    <p className="text-[13px] text-muted-foreground leading-snug">{result.voice_drift.description}</p>
                  )}
                </div>
              </SectionCard>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
